// frontend/src/pages/dashboard/DashboardPage.js
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { pollingManager } from '../../hooks/RealtimePoll';
import axios from "axios";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Skeleton,
  Avatar,
  Badge,
  LinearProgress,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  alpha,
  useColorScheme
} from "@mui/material";
import { Bar, Pie } from "react-chartjs-2";
import {
  Assignment,
  AssignmentTurnedIn,
  Folder,
  People,
  MonetizationOn,
  ReceiptLong,
  AccessTime,
  TrendingUp,
  ShowChart,
  Person,
  Business,
  CalendarToday,
  Add,
  MoreVert,
  CheckCircle,
  Timelapse,
  AccountBalance,
  LocalOffer,
  Description,
  Dashboard,
  Group,
  Payment,
  FilePresent,
  WorkOutline,
  Done,
  HourglassEmpty,
  PersonAdd,
  AttachMoney,
  Receipt,
  BarChart,
  PeopleAlt,
  CurrencyExchange
} from "@mui/icons-material";
import DashboardLayout from "../../components/layout/DashboardLayout";
import Loading from "../../components/common/Loading";
import { useAuth } from "../../context/AuthContext";
import { projectAPI, taskAPI, clientAPI, reportAPI, invoiceAPI, backlogAPI } from "../../services/api";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js/auto";

// Register chart components
ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

// Modern color palette
const COLORS = {
  primary: "#4361ee",
  secondary: "#3f37c9",
  success: "#4cc9f0",
  warning: "#f72585",
  info: "#4895ef",
  light: "#f8f9fa",
  dark: "#212529",
  gray: "#6c757d",
  cardBg: "#ffffff",
  surface: "#f0f4f8"
};

// Modern Stat Card Component
function StatCard({ title, value, icon: Icon, color, trend, loading = false }) {
  if (loading) {
    return (
      <Card sx={{
        borderRadius: 2,
        boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: `1px solid ${alpha('#000', 0.03)}`
      }}>
        <CardContent>
          <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="40%" height={30} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{
      borderRadius: 2,
      boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: `1px solid ${alpha('#000', 0.03)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`,
        zIndex: 1
      },
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 24px ${alpha('#000', 0.1)}`,
        '& .stat-icon': {
          transform: 'scale(1.1)',
        }
      }
    }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2, pt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box
            className="stat-icon"
            sx={{
              bgcolor: alpha(color, 0.1),
              width: 50,
              height: 50,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              transition: 'transform 0.3s ease'
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
          {trend && (
            <Chip
              size="small"
              label={trend.value}
              color={trend.type === 'up' ? 'success' : 'error'}
              sx={{
                fontWeight: 'bold',
                fontSize: '0.75rem',
                height: '24px'
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 0.5,
            fontSize: '0.875rem'
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          fontWeight="700"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' }
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

// Project Card Component
function ProjectCard({ project, isMobile }) {
  const navigate = useNavigate();
  const progress = project.progress || 0;
  const statusColor = project.status?.key === 'completed' ? '#4cc9f0' :
    project.status?.key === 'in_progress' ? '#4361ee' :
      '#f72585';

  return (
    <Card sx={{
      borderRadius: 2,
      boxShadow: `0 2px 8px ${alpha('#000', 0.05)}`,
      mb: 1,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: `1px solid ${alpha('#000', 0.03)}`,
      background: COLORS.cardBg,
      '&:hover': {
        transform: 'translateX(4px)',
        boxShadow: `0 6px 16px ${alpha('#000', 0.08)}`,
        borderColor: alpha(COLORS.primary, 0.2)
      }
    }}>
      <CardContent onClick={() => navigate(`/projects/${project?.id}/board`)} sx={{ cursor: 'pointer' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: alpha(statusColor, 0.1), width: 32, height: 32, color: statusColor }}>
              <Folder fontSize="small" />
            </Avatar>
            <Typography variant="subtitle2" fontWeight="600" noWrap>
              {project.name}
            </Typography>
          </Box>
          {!isMobile && (
            <Chip
              size="small"
              label={project.status?.name || 'Unknown'}
              sx={{
                bgcolor: alpha(statusColor, 0.1),
                color: statusColor,
                fontWeight: 'bold',
                fontSize: '0.75rem',
                height: '24px'
              }}
            />
          )}
        </Box>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Chip
              size="small"
              label={project.status?.name || 'Unknown'}
              sx={{
                bgcolor: alpha(statusColor, 0.1),
                color: statusColor,
                fontWeight: 'bold',
                fontSize: '0.75rem',
                height: '24px'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {progress}% Complete
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              flexGrow: 1,
              height: 8,
              borderRadius: 4,
              bgcolor: alpha('#000', 0.05),
              '& .MuiLinearProgress-bar': {
                bgcolor: statusColor,
                borderRadius: 4
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" minWidth="30px">
            {progress}%
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// Client Card Component
function ClientCard({ client, isMobile }) {
  return (
    <Card sx={{
      borderRadius: 2,
      boxShadow: `0 2px 8px ${alpha('#000', 0.05)}`,
      mb: 1,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: `1px solid ${alpha('#000', 0.03)}`,
      background: COLORS.cardBg,
      '&:hover': {
        transform: 'translateX(4px)',
        boxShadow: `0 6px 16px ${alpha('#000', 0.08)}`,
        borderColor: alpha(COLORS.primary, 0.2)
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: alpha(COLORS.primary, 0.1), width: 36, height: 36, color: COLORS.primary }}>
              <Person fontSize="small" />
            </Avatar>
            <Typography variant="subtitle2" fontWeight="600" noWrap>
              {client.name}
            </Typography>
          </Box>
          {isMobile && (
            <Typography variant="caption" color="text.secondary">
              {client.email?.substring(0, 8)}...
            </Typography>
          )}
        </Box>
        {!isMobile && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {client.email}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user, company, trialInfo } = useAuth();
  const isAdmin = user?.teamMembership?.role?.name?.toLowerCase() === "system administrator";
  const canViewBilling = user?.teamMembership?.role?.permissions?.some(
    p => p.name === 'billing.manage' || p.name === 'dashboard.view.page'
  ) || user?.isAdmin?.() || false;

  const canViewFinancials = useMemo(() => {
    if (isAdmin) return true;
    if (!canViewBilling) return false;
    const hasActiveTrial = trialInfo?.isActive || trialInfo?.daysRemaining > 0;
    const hasActiveSubscription = user?.subscription?.status === 'active' || company?.subscription_status === 'active';

    return hasActiveTrial || hasActiveSubscription;
  }, [isAdmin, canViewBilling, trialInfo, user, company]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    totalClients: 0,
    completedProjects: 0,
    pendingProjects: 0
  });
  const [reportData, setReportData] = useState(null);
  const [billingStats, setBillingStats] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({ open: false, invoiceId: null, amount: "" });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    loadAll();
    loadBacklogTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Fetch with pagination params to get all data
      const [projectsRes, tasksRes, clientsRes, dashboardRes, billingStatsRes, billingSummaryRes] = await Promise.allSettled([
        projectAPI.getAll({ page: 1, per_page: 100 }), // Get more items
        taskAPI.getAll({ page: 1, per_page: 100 }),
        clientAPI.getAll({ page: 1, per_page: 100 }),
        reportAPI.getDashboardStats(),
        reportAPI.getBillingStats?.(),
        reportAPI.getBillingSummary?.(),
      ]);

      // Handle paginated responses
      const projectsData = projectsRes.status === "fulfilled"
        ? (projectsRes.value.data?.data || projectsRes.value.data || [])
        : [];
      const tasksData = tasksRes.status === "fulfilled"
        ? (tasksRes.value.data?.data || tasksRes.value.data || [])
        : [];
      const clientsData = clientsRes.status === "fulfilled"
        ? (clientsRes.value.data?.data || clientsRes.value.data || [])
        : [];
      const dashboard = dashboardRes.status === "fulfilled" ? dashboardRes.value.data : null;
      const billingSummaryData = billingSummaryRes && billingSummaryRes.status === "fulfilled" ? billingSummaryRes.value.data : null;
      const billingStatsData = billingStatsRes && billingStatsRes.status === "fulfilled" ? billingStatsRes.value.data : null;

      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setReportData(dashboard);
      setBillingStats(billingStatsData);
      setBillingSummary(billingSummaryData);

      // Calculate stats from actual data
      setStats({
        totalProjects: projectsData.length,
        completedProjects: projectsData.filter(p => p.status?.key === "completed").length,
        pendingProjects: projectsData.filter(p => p.status?.key !== "completed").length,
        totalTasks: tasksData.length,
        completedTasks: tasksData.filter(t => t.status?.key === "task_done").length,
        pendingTasks: tasksData.filter(t => t.status?.key !== "task_done").length,
        totalClients: clientsData.length,
      });
    } catch (e) {
      console.error("Load error", e);
    } finally {
      setLoading(false);
    }
  };

  const [backlogTasks, setBacklogTasks] = useState([]);

  const loadBacklogTasks = async () => {
    try {
      // Fetch overdue tasks directly from the new endpoint
      const overdueRes = await reportAPI.getOverdueTasks();
      const overdueTasks = overdueRes.data?.tasks || [];

      // Transform to match the table structure
      const transformedTasks = overdueTasks.map(t => ({
        id: t.id,
        title: t.title,
        project_name: t.project?.name || 'Unknown',
        priority: t.priority || 'medium',
        story_points: t.story_points || 0,
        due_date: t.due_date,
        days_overdue: t.days_overdue,
        status: t.status
      }));

      setBacklogTasks(transformedTasks);
    } catch (e) {
      console.error("Overdue tasks load error", e);
      setBacklogTasks([]);
    }
  };

  // compute recent invoices/payments from billingSummary if available
  const recentInvoices = useMemo(() => {
    if (!billingSummary?.clients) return [];
    const invoices = [];
    billingSummary.clients.forEach(c => {
      c.projects.forEach(p => {
        p.invoices.forEach(inv => {
          invoices.push({
            client: c.name,
            project: p.name,
            ...inv,
          });
        });
      });
    });
    // newest first by issued_at (if present) or id
    return invoices.sort((a, b) => {
      const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
      const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
      return tb - ta || (b.id - a.id);
    }).slice(0, 10);
  }, [billingSummary]);

  const recentPayments = useMemo(() => {
    if (!billingSummary?.clients) return [];
    const payments = [];
    billingSummary.clients.forEach(c => {
      c.projects.forEach(p => {
        p.invoices.forEach(inv => {
          (inv.payments || []).forEach(pay => {
            payments.push({
              client: c.name,
              project: p.name,
              invoice_id: inv.id,
              ...pay,
            });
          });
        });
      });
    });
    return payments.sort((a, b) => {
      const ta = a.paid_at ? new Date(a.paid_at).getTime() : 0;
      const tb = b.paid_at ? new Date(b.paid_at).getTime() : 0;
      return tb - ta || (b.id - a.id);
    }).slice(0, 10);
  }, [billingSummary]);

  // handlers for payment modal
  const openPaymentModal = (invoiceId) => setPaymentModal({ open: true, invoiceId, amount: "" });
  const closePaymentModal = () => setPaymentModal({ open: false, invoiceId: null, amount: "" });

  const submitPayment = async () => {
    const amount = parseFloat(paymentModal.amount);
    if (!amount || amount <= 0) return alert("Enter a valid amount");
    setSavingPayment(true);
    try {
      const res = await invoiceAPI.markAsPaid(paymentModal.invoiceId, { amount });
      // Update billingSummary and stats live
      await loadAll(); // simplest approach: reload summary from server to guarantee correctness
      closePaymentModal();
    } catch (err) {
      console.error(err);
      alert("Payment failed: " + (err.response?.data?.error || err.message || "unknown"));
    } finally {
      setSavingPayment(false);
    }
  };

  // safe formatting
  const fmt = (currency, amount) => `${currency ?? "USD"} ${(Number(amount) || 0).toFixed(2)}`;

  useEffect(() => {

    return () => {
      pollingManager.stop("projects");
      pollingManager.stop("tasks");
    };
  }, []);



  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Skeleton variant="text" width="40%" height={40} sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            {[...Array(6)].map((_, i) => (
              <Grid item size={{ xs: 6, md: 2, sm: 4 }} key={i}>
                <StatCard loading />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{
        p: { xs: 1, sm: 2, md: 3 },
        background: COLORS.surface,
        minHeight: '100vh',
        pb: 10
      }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} color="text.primary">
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive overview of your projects, tasks, and financials.
          </Typography>
        </Box>

        {/* Top stat cards - Responsive grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 6, md: 2, sm: 4 }}>
            <StatCard
              title="Total Projects"
              value={stats.totalProjects}
              icon={WorkOutline}
              color={COLORS.primary}
            // trend={{ value: "+12%", type: "up" }}
            />
          </Grid>
          <Grid item size={{ xs: 6, md: 2, sm: 4 }}>
            <StatCard
              title="Completed Projects"
              value={stats.completedProjects}
              icon={Done}
              color={COLORS.success}
            // trend={{ value: "+8%", type: "up" }}
            />
          </Grid>
          <Grid item size={{ xs: 6, md: 2, sm: 4 }}>
            <StatCard
              title="Pending Projects"
              value={stats.pendingProjects}
              icon={HourglassEmpty}
              color={COLORS.warning}
            // trend={{ value: "-5%", type: "down" }}
            />
          </Grid>

          {/* Billing cards (admin only) */}
          {isAdmin && canViewBilling && (
            <>
              <Grid item size={{ xs: 6, md: 2, sm: 4 }}>
                <StatCard
                  title="Total Paid"
                  value={billingStats ? fmt(billingStats.currency, billingStats.total_paid) : "USD 0.00"}
                  icon={AttachMoney}
                  color={COLORS.success}
                // trend={{ value: "+25%", type: "up" }}
                />
              </Grid>
              <Grid item size={{ xs: 6, md: 2, sm: 4 }}>
                <StatCard
                  title="Total Due"
                  value={billingStats ? fmt(billingStats.currency, billingStats.total_due) : "USD 0.00"}
                  icon={CurrencyExchange}
                  color={COLORS.warning}
                // trend={{ value: "-15%", type: "down" }}
                />
              </Grid>
              <Grid item size={{ xs: 6, md: 2, sm: 4 }}>
                <StatCard
                  title="Invoices"
                  value={billingStats ? billingStats.total_invoices : 0}
                  icon={Receipt}
                  color={COLORS.gray}
                // trend={{ value: "+10%", type: "up" }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* Backlog Tasks */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Backlog ({backlogTasks.length})
            </Typography>
            {backlogTasks.length > 0 && (
              <Chip
                label={`${backlogTasks.length} Backlog`}
                color="error"
                size="small"
              />
            )}
          </Box>
          {backlogTasks.length === 0 ? (
            <Typography color="text.secondary">No overdue tasks - Great job! 🎉</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Story Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backlogTasks.map(task => (
                    <TableRow
                      key={task.id}
                      sx={{
                        bgcolor: task.days_overdue > 7
                          ? alpha('#f72585', 0.05)
                          : task.days_overdue > 3
                            ? alpha('#f72585', 0.03)
                            : 'transparent'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {task.title}
                        </Typography>
                        {task.days_overdue > 0 && (
                          <Typography variant="caption" color="error">
                            ⚠️ {task.days_overdue} days overdue
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{task.project_name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={task.priority}
                          color={
                            task.priority === 'urgent' ? 'error' :
                              task.priority === 'high' ? 'warning' :
                                'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color={task.days_overdue > 0 ? 'error' : 'text.secondary'}
                          fontWeight={task.days_overdue > 0 ? 600 : 400}
                        >
                          {task.due_date || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{task.story_points || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Main content grid */}
        <Grid container spacing={2}>
          {/* Left column: projects + charts */}
          <Grid item size={{ xs: 12, md: 8 }}>
            {/* Projects Overview */}
            <Paper sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
              background: COLORS.cardBg,
              border: `1px solid ${alpha('#000', 0.03)}`
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} color="text.primary">Projects Overview</Typography>
                {/* <Button 
                  size="small" 
                  startIcon={<Add />}
                  onClick={() => {}}
                  sx={{ 
                    textTransform: 'none',
                    background: alpha(COLORS.primary, 0.1),
                    color: COLORS.primary,
                    '&:hover': {
                      background: alpha(COLORS.primary, 0.2)
                    }
                  }}
                >
                  New Project
                </Button> */}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {projects.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Folder sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary">No projects yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Create your first project to get started
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  maxHeight: 300,
                  overflowY: 'auto',
                  pr: 1
                }}>
                  {projects.slice(0, 6).map(p => (
                    <ProjectCard key={p.id} project={p} isMobile={isMobile} />
                  ))}
                </Box>
              )}
            </Paper>

            {/* Charts section */}
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Paper sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
                  background: COLORS.cardBg,
                  border: `1px solid ${alpha('#000', 0.03)}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">Tasks by Status</Typography>
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {reportData?.tasks_by_status ? (
                    <Box sx={{ height: isMobile ? 200 : 220 }}>
                      <Pie data={{
                        labels: Object.keys(reportData.tasks_by_status),
                        datasets: [{
                          data: Object.values(reportData.tasks_by_status),
                          backgroundColor: ["#4cc9f0", "#f72585", "#4361ee", "#6c757d"],
                          borderWidth: 0
                        }]
                      }} options={{
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              padding: 10,
                              font: {
                                size: 12
                              }
                            }
                          }
                        },
                        responsive: true,
                        maintainAspectRatio: false
                      }} />
                    </Box>
                  ) : <Typography color="text.secondary">No data</Typography>}
                </Paper>
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <Paper sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
                  background: COLORS.cardBg,
                  border: `1px solid ${alpha('#000', 0.03)}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">Top Users (Tasks)</Typography>
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {reportData?.user_task_counts ? (
                    <Box sx={{ height: isMobile ? 200 : 220 }}>
                      <Bar data={{
                        labels: reportData.user_task_counts.map(u => u.name),
                        datasets: [{
                          label: "Tasks",
                          data: reportData.user_task_counts.map(u => u.task_count),
                          backgroundColor: "#3f37c9",
                          borderRadius: 4
                        }]
                      }} options={{
                        plugins: {
                          legend: {
                            display: false
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              drawBorder: false
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            }
                          }
                        },
                        responsive: true,
                        maintainAspectRatio: false
                      }} />
                    </Box>
                  ) : <Typography color="text.secondary">No data</Typography>}
                </Paper>
              </Grid>
            </Grid>
          </Grid>


          {/* Right column: clients + billing summary + recent payments */}
          <Grid item size={{ xs: 12, md: 4 }}>
            {isAdmin && canViewFinancials && (
              <>
                {/* Billing Snapshot */}
                <Paper sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
                  background: COLORS.cardBg,
                  border: `1px solid ${alpha('#000', 0.03)}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">Billing Snapshot</Typography>
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {billingStats ? fmt(billingStats.currency, billingStats.total_paid) : "USD 0.00"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">Total Due</Typography>
                      <Typography variant="body2" fontWeight={700} color="warning.main">
                        {billingStats ? fmt(billingStats.currency, billingStats.total_due) : "USD 0.00"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">Invoices</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {billingStats ? billingStats.total_invoices : 0}
                      </Typography>
                    </Box>

                    {/* <Button 
                  size="small" 
                  variant="contained" 
                  sx={{ 
                    mt: 1, 
                    textTransform: 'none',
                    background: alpha(COLORS.primary, 0.1),
                    color: COLORS.primary,
                    '&:hover': {
                      background: alpha(COLORS.primary, 0.2)
                    }
                  }}
                  onClick={() => {  }}
                >
                  Open Billing
                </Button> */}
                  </Box>
                </Paper>

                {/* Recent Payments */}
                <Paper sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
                  background: COLORS.cardBg,
                  border: `1px solid ${alpha('#000', 0.03)}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">Recent Payments</Typography>
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {recentPayments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">No recent payments</Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: 240 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Client</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentPayments.map(p => (
                            <TableRow key={p.id ?? `${p.invoice_id}_${p.paid_at}`}>
                              <TableCell>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "-"}</TableCell>
                              <TableCell>{fmt(p.currency, p.amount)}</TableCell>
                              <TableCell>{p.client}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </>
            )}
          </Grid>
        </Grid>

        {isAdmin && canViewFinancials && (
          <>
            {/* Recent Invoices */}
            <Paper sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha('#000', 0.05)}`,
              background: COLORS.cardBg,
              border: `1px solid ${alpha('#000', 0.03)}`
            }}>
              <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1
              }}>
                <Typography variant="h6" fontWeight={700} color="text.primary">Recent Invoices</Typography>
                {/* <Button 
              size="small" 
              variant="outlined" 
              onClick={() => {  }}
              sx={{ 
                textTransform: 'none',
                borderColor: alpha(COLORS.primary, 0.3),
                color: COLORS.primary,
                '&:hover': {
                  borderColor: COLORS.primary,
                  background: alpha(COLORS.primary, 0.05)
                }
              }}
            >
              View All
            </Button> */}
              </Box>

              {recentInvoices.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ReceiptLong sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary">No invoices yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Create your first invoice to start tracking payments
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Paid</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentInvoices.map(inv => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.number ?? inv.id}</TableCell>
                          <TableCell>{inv.client}</TableCell>
                          <TableCell>{inv.project}</TableCell>
                          <TableCell>{fmt(inv.currency, inv.amount)}</TableCell>
                          <TableCell>{fmt(inv.currency, inv.paid_amount ?? 0)}</TableCell>
                          <TableCell>
                            <Chip
                              label={(inv.status || "pending").toUpperCase()}
                              size="small"
                              color={inv.status === "paid" ? "success" : inv.status === "partial" ? "info" : "warning"}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => openPaymentModal(inv.id)}
                              sx={{
                                color: COLORS.primary,
                                '&:hover': {
                                  background: alpha(COLORS.primary, 0.05)
                                }
                              }}
                            >
                              Record Payment
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Payment modal */}
            <Dialog open={paymentModal.open} onClose={closePaymentModal}>
              <DialogTitle>Record Payment</DialogTitle>
              <DialogContent>
                <TextField
                  label="Amount"
                  type="number"
                  fullWidth
                  value={paymentModal.amount}
                  onChange={(e) => setPaymentModal(prev => ({ ...prev, amount: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">{billingStats?.currency ?? "USD"}</InputAdornment> }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closePaymentModal}>Cancel</Button>
                <Button onClick={submitPayment} disabled={savingPayment} variant="contained">
                  {savingPayment ? "Saving..." : "Record Payment"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
}